import { tagNameEquals } from '@/lib/tag'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { useAtomValue } from 'jotai'
import client from '@/services/client.service'
import { Event, verifyEvent } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { useEffect, useMemo, useState } from 'react'
import MainNoteCard from './MainNoteCard'
import PendingUndoButton from '../PendingUndoButton'

export default function RepostNoteCard({
  event,
  className,
  filterMutedNotes = true,
  pinned = false,
  onTargetEventLoaded,
  groupedNotesTotalCount,
  onAllNotesRead,
  areAllNotesRead,
  displayScore,
  clamp = false
}: {
  event: Event
  className?: string
  filterMutedNotes?: boolean
  pinned?: boolean
  onTargetEventLoaded?: (event: Event) => void
  groupedNotesTotalCount?: number
  onAllNotesRead?: () => void
  areAllNotesRead?: boolean
  displayScore?: number
  clamp?: boolean
}) {
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const [targetEvent, setTargetEvent] = useState<Event | null>(null)

  const shouldHide = useMemo(() => {
    if (!targetEvent) return true
    if (filterMutedNotes && mutePubkeySet.has(targetEvent.pubkey)) {
      return true
    }
    return false
  }, [targetEvent, filterMutedNotes, mutePubkeySet])

  useEffect(() => {
    const fetch = async () => {
      try {
        const eventFromContent = event.content ? (JSON.parse(event.content) as Event) : null
        if (eventFromContent && verifyEvent(eventFromContent)) {
          if (eventFromContent.kind === kinds.Repost) {
            return
          }
          const targetSeenOn = client.getSeenEventRelays(eventFromContent.id)
          if (targetSeenOn.length === 0) {
            const seenOn = client.getSeenEventRelays(event.id)
            for (const relay of seenOn) {
              client.trackEventSeenOn(eventFromContent.id, relay)
            }
          }
          setTargetEvent(eventFromContent)
          onTargetEventLoaded?.(eventFromContent)
          client.addEventToCache(eventFromContent)
          return
        }

        const [, id, relay, , pubkey] = event.tags.find(tagNameEquals('e')) ?? []
        if (!id) {
          return
        }
        const targetEvent = await client.fetchEvent({
          id,
          relays: relay ? [relay] : [],
          author: pubkey
        })
        if (targetEvent) {
          setTargetEvent(targetEvent)
          onTargetEventLoaded?.(targetEvent)
        }
      } catch {
        // ignore
      }
    }
    fetch()
  }, [event])

  const reposterPubkeys = useMemo(() => [event.pubkey], [event.pubkey])

  if (!targetEvent || shouldHide) return null

  return (
    <>
      <PendingUndoButton event={event} />
      <MainNoteCard
        className={className}
        reposters={reposterPubkeys}
        repostEvent={event}
        event={targetEvent}
        pinned={pinned}
        groupedNotesTotalCount={groupedNotesTotalCount}
        onAllNotesRead={onAllNotesRead}
        areAllNotesRead={areAllNotesRead}
        displayScore={displayScore}
        clamp={clamp}
      />
    </>
  )
}
